import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { useToast } from '../components/toast/Toast';
import { exportsApi } from '../api/modules';
import { relativeTime } from '../lib/format';

interface Template {
  id: string;
  title: string;
  desc: string;
  icon: string;
  module: string;
  format: 'xlsx' | 'pdf';
  est: string;
  /** Backend path to POST against */
  path: string;
  /** Suggested filename for the download */
  filename: string;
}

const TEMPLATES: Template[] = [
  { id: 'R01', title: 'Director Monthly Cockpit',  desc: 'Full state overview · KPIs · scheme progress · alerts · AI recommendations',  icon: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',                  module: 'Overview',  format: 'pdf',  est: '1 page',         path: '/exports/pdf/cockpit',         filename: 'director-cockpit.pdf' },
  { id: 'R02', title: 'Scheme Progress Statement', desc: 'Goat / Sheep / Pig — target vs. verified · district-wise pipeline',            icon: 'M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3M3 12h18l-2 7H5l-2-7z',          module: 'M02',       format: 'xlsx', est: '~1.5k rows',      path: '/exports/excel/schemes',       filename: 'scheme-progress.xlsx' },
  { id: 'R04', title: 'MAITRI Activity Report',    desc: 'Worker-wise services · inactivity · Bharat Pashudhan sync',                     icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', module: 'M04', format: 'xlsx', est: '~2,148 rows',     path: '/exports/excel/maitri',        filename: 'maitri-activity.xlsx' },
  { id: 'R06', title: 'SLA Compliance Audit',      desc: 'Stage-wise SLA breaches · owner accountability · escalation chains',           icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5',                module: 'M08',       format: 'xlsx', est: '~50 rows',        path: '/exports/excel/alerts',        filename: 'sla-audit.xlsx' },
  { id: 'R02b', title: 'Beneficiaries Register',   desc: 'Full lifecycle register — every beneficiary with stage + subsidy + location',  icon: 'M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3M3 12h18l-2 7H5l-2-7z',          module: 'M02',       format: 'xlsx', est: '~13,246 rows',    path: '/exports/excel/beneficiaries', filename: 'beneficiaries.xlsx' },
];

const COMING_SOON: Pick<Template, 'id' | 'title' | 'desc' | 'icon' | 'module' | 'format' | 'est'>[] = [
  { id: 'R03', title: 'Poultry DPR Register',      desc: 'Stage-wise DPR list · committee notes · approved amounts',          icon: 'M12 2a10 10 0 1 0 0 20M12 8v4l2 2',                          module: 'M03',     format: 'xlsx', est: '~150 rows'  },
  { id: 'R05', title: 'Mela Camp Summary',         desc: 'Per-camp outcomes · target vs. served · disease surveillance',      icon: 'M3 4h18v18H3zM16 2v4M8 2v4M3 10h18',                          module: 'M05',     format: 'pdf',  est: '~30 pages'   },
  { id: 'R07', title: 'AI Recommendations Log',    desc: 'Open / accepted / rejected with reasoning · acceptance trend',      icon: 'M12 3l1.91 5.27L19 9.27l-4 4.4 1.2 5.33L12 16.27l-5.2 2.73L8 13.67 4 9.27z', module: 'CCC-AI',  format: 'pdf', est: '~6 pages' },
  { id: 'R08', title: 'Audit Log Extract',         desc: 'Full audit trail by date range · user activity · access history',   icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', module: 'Tools',   format: 'xlsx', est: 'on demand'  },
];

const SCHEDULED = [
  { id: 'SCH-001', title: 'Director Monthly Cockpit',    cadence: 'First Monday, 06:00',  recipients: 'Director, Pr. Secretary, Minister' },
  { id: 'SCH-002', title: 'SLA Compliance Audit',         cadence: 'Every Friday, 17:00',  recipients: 'Director, Addl. Directors' },
  { id: 'SCH-003', title: 'AI Recommendations Summary',   cadence: 'Daily, 08:00',          recipients: 'Director, AI ops team' },
  { id: 'SCH-004', title: 'Scheme Progress (per mandal)', cadence: 'Quarterly',             recipients: 'Each Addl. Director' },
];

function bytes(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function ReportsPage() {
  const { show } = useToast();
  const recent = useQuery({
    queryKey: ['exports', 'recent'],
    queryFn: () => exportsApi.recent(15),
    refetchInterval: 30_000,
  });

  async function runExport(t: Template) {
    show(`Generating ${t.title}…`);
    try {
      const code = await exportsApi.download(t.path, t.filename);
      show(`Download started · ${code || t.filename}`);
      void recent.refetch();
    } catch {
      show('Export failed · try again');
    }
  }

  return (
    <>
      <Header breadcrumbs={['Tools', 'Reports & Exports']} />
      <div className="px-8 pt-7 pb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">Tools</div>
          <h1 className="serif-display text-3xl text-navy-800 font-medium">Reports &amp; Exports</h1>
          <p className="text-sm text-navy-800/60 mt-1">
            One-click Excel + PDF exports · scheduled runs · audit-trailed history
          </p>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Live templates */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="serif-display text-base text-navy-800 font-medium">Available exports</h3>
            <span className="text-[10px] uppercase tracking-widest text-saffron-700 font-medium">LIVE — wired to backend</span>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {TEMPLATES.map((t) => (
              <div key={t.id} className="bg-white border border-cream-200 rounded-sm p-4 hover:shadow-md transition">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-sm bg-saffron-50 flex items-center justify-center flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E8740C" strokeWidth="2"><path d={t.icon} /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-navy-800/40 font-numeric">{t.id} · {t.module}</div>
                    <div className="text-sm font-medium text-navy-800 leading-tight mt-0.5">{t.title}</div>
                  </div>
                </div>
                <p className="text-xs text-navy-800/70 leading-relaxed">{t.desc}</p>
                <div className="mt-3 pt-3 border-t border-cream-100 flex items-center justify-between text-[11px]">
                  <span className="text-navy-800/50 font-numeric uppercase tracking-wider">{t.format} · {t.est}</span>
                  <button
                    type="button"
                    onClick={() => runExport(t)}
                    className="text-saffron-600 hover:text-saffron-700 font-medium"
                  >
                    Download →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Coming soon templates */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="serif-display text-base text-navy-800 font-medium">Coming soon</h3>
            <span className="text-[10px] uppercase tracking-widest text-navy-800/40 font-medium">Day 7 · Day 8</span>
          </div>
          <div className="grid grid-cols-4 gap-3 opacity-70">
            {COMING_SOON.map((t) => (
              <div key={t.id} className="bg-cream-50 border border-cream-200 rounded-sm p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-sm bg-cream-100 flex items-center justify-center flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5A5A5A" strokeWidth="2"><path d={t.icon} /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-navy-800/40 font-numeric">{t.id} · {t.module}</div>
                    <div className="text-sm font-medium text-navy-800/70 leading-tight mt-0.5">{t.title}</div>
                  </div>
                </div>
                <p className="text-xs text-navy-800/60 leading-relaxed">{t.desc}</p>
                <div className="mt-3 pt-3 border-t border-cream-100 text-[11px] text-navy-800/50 font-numeric uppercase tracking-wider">
                  {t.format} · {t.est}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recent + Scheduled */}
        <section className="grid grid-cols-3 gap-5">
          <div className="col-span-2 bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100 flex items-center justify-between">
              <div>
                <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Recent exports</h3>
                <p className="text-xs text-navy-800/60 mt-3">Live · auto-refreshes every 30 s</p>
              </div>
              <button
                type="button"
                onClick={() => void recent.refetch()}
                className="text-xs text-saffron-600 hover:text-saffron-700 font-medium"
              >
                Refresh
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-cream-50">
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">ID</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Report</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">User</th>
                  <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Rows</th>
                  <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Size</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">When</th>
                  <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.isLoading || !recent.data ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-t border-cream-100"><td colSpan={7} className="px-3 py-3"><div className="h-3 bg-cream-100 rounded animate-pulse" /></td></tr>
                  ))
                ) : recent.data.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-6 text-center text-sm text-navy-800/50">No exports yet — try the templates above</td></tr>
                ) : recent.data.map((e) => (
                  <tr key={e.id} className="border-t border-cream-100 hover:bg-cream-50">
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">{e.id}</td>
                    <td className="px-3 py-2.5 font-medium text-navy-800 text-xs">{e.name}</td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs font-numeric">{e.username}</td>
                    <td className="px-3 py-2.5 text-right text-navy-800/70 text-xs font-numeric">{e.row_count?.toLocaleString('en-IN') ?? '—'}</td>
                    <td className="px-3 py-2.5 text-right text-navy-800/70 text-xs font-numeric">{bytes(e.size_bytes)}</td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs font-numeric">{relativeTime(e.requested_at)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`pill ${e.status === 'completed' ? 'pill-green' : e.status === 'failed' ? 'pill-red' : 'pill-amber'}`}>{e.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-cream-200 rounded-sm">
            <div className="px-5 py-4 border-b border-cream-100">
              <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Scheduled</h3>
              <p className="text-xs text-navy-800/60 mt-3">Auto-runs · emailed to recipients</p>
            </div>
            <div className="divide-y divide-cream-100">
              {SCHEDULED.map((s) => (
                <div key={s.id} className="px-5 py-3.5">
                  <div className="text-sm font-medium text-navy-800">{s.title}</div>
                  <div className="text-[11px] text-navy-800/60 mt-1.5 font-numeric flex items-center gap-1.5">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                    {s.cadence}
                  </div>
                  <div className="text-[11px] text-navy-800/50 mt-1">{s.recipients}</div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => show('Schedule builder coming in Day 7')}
                className="w-full px-5 py-3 text-xs text-saffron-600 hover:bg-saffron-50 transition font-medium"
              >
                + Add scheduled report
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
